import { InfoRow } from "./InfoRow";
import { Divider, FormInstance, Image, Input, Select } from "antd";
import { useTranslation } from "react-i18next";
import creditMethod from "../../../assets/payment-method/credit-card.png";
import gpay from "../../../assets/payment-method/gpay-method.png";
import applepay from "../../../assets/payment-method/applepay-method.png";

import { AffiliateCodeInput } from "../../../components";
import { useState } from "react";
import dayjs from "dayjs";
import CreditSquareupButton from "../../../components/common/Button/CreditSquareupButton";
import GooglePayButton from "../../../components/common/Button/GooglePaySquareupButton";
import ApplePaySquareupButton from "../../../components/common/Button/ApplePaySquareupButton";
import visaRequestServices from "../../../api/services/visaRequestServices";

// import ImagePolicy01 from "../../../assets/policies/policy_guide_01.png";
// import ImagePolicy04 from "../../../assets/policies/policy_guide_04.png";
// import ImagePolicy11 from "../../../assets/policies/policy_guide_11.png";
// import ImagePolicy12 from "../../../assets/policies/policy_guide_12.png";

interface PaymentDetailField {
  label: string;
  value: string;
  editable?: boolean;
}

interface PaymentDetailProps {
  form: FormInstance<IVisaRequestFormData>;
  agreePolicy: boolean;
  setAgreePolicy: React.Dispatch<React.SetStateAction<boolean>>;
  selectedPaymentMethod: string;
  setSelectedPaymentMethod: React.Dispatch<React.SetStateAction<string>>;
  paymentInfo: IPaymentInfo;
  setPaymentInfo: React.Dispatch<React.SetStateAction<IPaymentInfo>>;
  setIsComplete: React.Dispatch<React.SetStateAction<boolean>>;
  setPaymentData: React.Dispatch<React.SetStateAction<any>>;
  handleFormSubmit?: () => void;
}

const visaEntryTypeMap = new Map([
  ["OneMonthSingle", "1 month - Single Entry"],
  ["OneMonthMultiple", "1 month - Multiple Entries"],
  ["ThreeMonthSingle", "3 months - Single Entry"],
  ["ThreeMonthMultiple", "3 months - Multiple Entries"],
]);

const visaEntryTypeFeeMap = new Map([
  ["OneMonthSingle", 54],
  ["OneMonthMultiple", 84],
  ["ThreeMonthSingle", 94],
  ["ThreeMonthMultiple", 104],
]);
const processingTimeFeeMap = new Map([
  ["Standard", 0],
  ["Express", 45],
  ["Urgent", 85],
]);

const PaymentDetail: React.FC<PaymentDetailProps> = ({
  form,
  agreePolicy,
  setAgreePolicy,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  paymentInfo,
  setPaymentInfo,
  setIsComplete,
  setPaymentData,
  handleFormSubmit,
}) => {
  const { t } = useTranslation();
  const [forceRenderState, setForceRenderState] = useState(false);

  const PaymentDetailFields: PaymentDetailField[] = [
    // {
    //   label: t("registration_form.step4.emergency_fullname"),
    //   value: `${form.getFieldValue("surname")} ${form.getFieldValue(
    //     "middle_and_given_name"
    //   )}`,
    //   editable: true
    // },
    // {
    //   label: t("registration_form.step3.passport_number"),
    //   value: form.getFieldValue("request_passport_number") || "",
    //   editable: true
    // },
    // {
    //   label: t("payment_details.visa_code"),
    //   value: "TEMP983258235308",
    //   editable: true
    // },
    // {
    //   label: t("payment_details.passport_type"),
    //   value: form.getFieldValue("passport_type") || "",
    //   editable: true
    // },
    // {
    //   label: t("registration_form.step2.date_of_birth"),
    //   value: form.getFieldValue("date_of_birth")
    //     ? form.getFieldValue("date_of_birth").format("DD/MM/YYYY")
    //     : "",
    //   editable: true
    // },
    // {
    //   label: t("registration_form.step2.email"),
    //   value: form.getFieldValue("email") || "",
    //   editable: true
    // },
    // {
    //   label: t("registration_form.step2.nationality"),
    //   value: form.getFieldValue("nationality") || "",
    //   editable: true
    // },
    {
      label: t("payment_details.to_issue_visa_for"),
      value: form.getFieldValue("evisa_entry_type")
        ? visaEntryTypeMap.get(form.getFieldValue("evisa_entry_type"))
        : "",
    },
    {
      label: t("payment_details.processing_type"),
      value: `${form.getFieldValue("processing_type") || ""} Processing`,
    },
    {
      label: t("payment_details.date_of_entry"),
      value: form.getFieldValue("intended_date_of_entry")
        ? form.getFieldValue("intended_date_of_entry").format("DD/MM/YYYY")
        : "",
    },
    {
      label: t("payment_details.visa_return_date"),
      value: dayjs()
        .add(
          form.getFieldValue("processing_type") === "Standard"
            ? 5
            : form.getFieldValue("processing_type") === "Express"
            ? 2
            : form.getFieldValue("processing_type") === "Urgent"
            ? 1
            : 0,
          "day"
        )
        .format("DD/MM/YYYY"),
    },
  ];

  const calculateTotalFee = () => {
    const visaFee =
      visaEntryTypeFeeMap.get(
        form.getFieldValue("evisa_entry_type") || "OneMonthSingle"
      ) || 0;
    const processingFee =
      processingTimeFeeMap.get(
        form.getFieldValue("processing_type") || "Standard"
      ) || 0;
    const voucherCode = paymentInfo.voucher_code ? 10 : 0;

    return visaFee + processingFee - voucherCode;
  };

  const handleChangeVisaType = async (value: string) => {
    form.setFieldsValue({
      evisa_entry_type: value as EVisaEntryType,
    });
    const formData = new FormData();
    formData.append("evisa_entry_type", value);
    formData.append(
      "date_of_birth",
      form.getFieldValue("date_of_birth").format("YYYY-MM-DD")
    );
    formData.append(
      "date_of_issue",
      form.getFieldValue("date_of_issue").format("YYYY-MM-DD")
    );
    formData.append(
      "passport_expiry_date",
      form.getFieldValue("passport_expiry_date").format("YYYY-MM-DD")
    );
    formData.append(
      "intended_date_of_entry",
      form.getFieldValue("intended_date_of_entry").format("YYYY-MM-DD")
    );
    formData.append("self_image", form.getFieldValue("self_image"));
    formData.append("passport_image", form.getFieldValue("passport_image"));

    await visaRequestServices.updateVisaRequest(
      paymentInfo.request_id.toString(),
      formData
    );
    setForceRenderState(!forceRenderState);
  };
  const handleChangeProcessingType = async (value: string) => {
    form.setFieldsValue({
      processing_type: value as EVisaProcessingType,
    });
    const formData = new FormData();
    formData.append("processing_type", value);
    formData.append(
      "date_of_birth",
      form.getFieldValue("date_of_birth").format("YYYY-MM-DD")
    );
    formData.append(
      "date_of_issue",
      form.getFieldValue("date_of_issue").format("YYYY-MM-DD")
    );
    formData.append(
      "passport_expiry_date",
      form.getFieldValue("passport_expiry_date").format("YYYY-MM-DD")
    );
    formData.append(
      "intended_date_of_entry",
      form.getFieldValue("intended_date_of_entry").format("YYYY-MM-DD")
    );
    formData.append("self_image", form.getFieldValue("self_image"));
    formData.append("passport_image", form.getFieldValue("passport_image"));

    await visaRequestServices.updateVisaRequest(
      paymentInfo.request_id.toString(),
      formData
    );
    setForceRenderState(!forceRenderState);
  };

  return (
    <div className="max-w-7xl mx-auto py-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* <div className="col-span-1 md:col-span-2 grid grid-cols-4 bg-blue-100 p-4 justify-center rounded-lg">
          <div className="col-span-1 text-primary-blue text-center font-bold text-base">
            {t("payment_details.content_billing")}
          </div>
          <div className="col-span-1 text-primary-blue text-center font-bold text-lg">
            {t("payment_details.visa_fee")}
          </div>
          <div className="col-span-1 text-primary-blue text-center font-bold text-base">
            {t("payment_details.amount")}
          </div>
          <div className="col-span-1 text-primary-blue text-center font-bold text-lg">
            39.50$
          </div>
        </div> */}

        {/* Bank Images */}
        {/* <div className="col-span-1 md:col-span-2 flex justify-center items-center mt-8">
          <div className="max-w-outer">
            <div className="flex flex-col justify-center items-center">
              <span className="text-2xl mb-4 fond-bold text-center">
                {t("policy.payment.section1.title")}
              </span>
              <img
                src={ImagePolicy11}
                alt="Bank Connect"
                className="w-full h-auto max-w-[1320px] max-h-[300px]"
              />
              <img
                src={ImagePolicy12}
                alt="Bank Connect"
                className="w-full h-auto max-w-[1320px] max-h-[300px]"
              />
            </div>
            <div className="w-full flex flex-col justify-center items-center">
              <span className="text-2xl mb-4 fond-bold text-center">
                {t("policy.payment.section2.title")}
              </span>
              <img
                src={ImagePolicy04}
                alt="Bank Connect"
                className="w-full h-auto max-w-[1725px] max-h-[650px]"
              />
            </div>
            <div className="w-full flex flex-col justify-center items-center">
              <span className="text-2xl mb-4 fond-bold text-center">
                {t("policy.payment.section3.title")}
              </span>
              <img
                src={ImagePolicy01}
                alt="Bank Connect"
                className="w-full h-auto max-w-[850px] max-h-[103px]"
              />
            </div>
          </div>
        </div> */}

        {/* Order Summary */}
        <div className="w-full col-span-1 md:col-span-2 grid md:grid-cols-2 grid-cols-1 gap-8 text-[#074799]">
          {/* Left Side */}
          <div className="flex flex-col gap-5">
            {/* Payment Method */}
            {/* <p className="text-xl font-semibold text-primary-blue">
              {t("policy.payment.method1_content.step2.title")}
            </p>
            <div className="flex justify-center items-center border rounded-xl px-6 py-2 gap-4">
              <Input
                type="radio"
                className="w-4"
                checked={selectedPaymentMethod === "Paypal"}
                onChange={() => setSelectedPaymentMethod("Paypal")}
              />

              <div className="w-full flex justify-between items-center">
                <div className="flex gap-2 text-base">Paypal</div>
                <div className="flex gap-4">
                  <Image
                    src={paypalMethod}
                    alt="paypal"
                    preview={false}
                    height={40}
                  />
                </div>
              </div>
            </div> */}
            <div className="flex justify-center items-center border rounded-xl px-6 py-2 gap-4">
              <Input
                type="radio"
                className="w-4"
                checked={selectedPaymentMethod === "Google Pay"}
                onChange={() => setSelectedPaymentMethod("Google Pay")}
              />

              <div className="w-full flex justify-between items-center">
                <div className="flex gap-2 text-base">Google Pay</div>

                <div className="flex gap-4">
                  <Image
                    src={gpay}
                    alt="gpay"
                    preview={false}
                    height={25}
                    width={58}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-center items-center border rounded-xl px-6 py-2 gap-4">
              <Input
                type="radio"
                className="w-4"
                checked={selectedPaymentMethod === "Apple Pay"}
                onChange={() => setSelectedPaymentMethod("Apple Pay")}
              />

              <div className="w-full flex justify-between items-center">
                <div className="flex gap-2 text-base">Apple Pay</div>

                <div className="flex gap-4">
                  <Image
                    src={applepay}
                    alt="apay"
                    preview={false}
                    height={25}
                    width={58}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-center items-center border rounded-xl px-6 py-2 gap-4">
              <Input
                type="radio"
                className="w-4"
                checked={selectedPaymentMethod === "Credit"}
                onChange={() => setSelectedPaymentMethod("Credit")}
              />

              <div className="w-full flex justify-between items-center">
                <div className="flex gap-2 text-base">Credit card</div>

                <div className="flex gap-4">
                  <Image
                    src={creditMethod}
                    alt="credit card"
                    preview={false}
                    width={58}
                    height={40}
                    className="border-2 object-cover p-1 rounded-xl"
                  />
                </div>
              </div>
            </div>

            {/* Card Info Section */}
            {/* {selectedPaymentMethod === "Paypal" && (
              <PaypalButton
                agreePolicy={agreePolicy}
                setIsComplete={setIsComplete}
                setPaymentData={setPaymentData}
                paymentInfo={paymentInfo}
                handleFormSubmit={handleFormSubmit}
              />
            )} */}
            {selectedPaymentMethod === "Google Pay" && (
              <GooglePayButton
                setIsComplete={setIsComplete}
                setPaymentData={setPaymentData}
                agreePolicy={agreePolicy}
                paymentInfo={paymentInfo}
                amount={calculateTotalFee()}
                handleFormSubmit={handleFormSubmit}
              />
            )}
            {selectedPaymentMethod === "Apple Pay" && (
              <ApplePaySquareupButton
                setIsComplete={setIsComplete}
                setPaymentData={setPaymentData}
                agreePolicy={agreePolicy}
                paymentInfo={paymentInfo}
                amount={calculateTotalFee()}
                handleFormSubmit={handleFormSubmit}
              />
            )}
            {selectedPaymentMethod === "Credit" && (
              <div className="w-full grid grid-cols-1 gap-6 p-8 border rounded-xl">
                {/* Card Number */}
                {/* <div className="col-span-4 flex flex-col">
                  <Typography.Title
                    style={{
                      fontSize: "16px",
                      fontFamily: "Plus Jakarta Sans, serif",
                      color: "#074799",
                      fontWeight: "400",
                    }}
                  >
                    {t("select_payment_option.card_number")}{" "}
                    <span className="text-[#FF8383]">*</span>
                  </Typography.Title>
                  <Input
                    placeholder="0000 0000 0000 0000"
                    className="border-[#074799]"
                    style={{
                      fontSize: "16px",
                      fontFamily: "Plus Jakarta Sans, serif",
                    }}
                  />
                </div> */}

                {/* Card Name */}
                {/* <div className="col-span-2 flex flex-col">
                  <Typography.Title
                    style={{
                      fontSize: "16px",
                      fontFamily: "Plus Jakarta Sans, serif",
                      color: "#074799",
                      fontWeight: "400",
                    }}
                  >
                    {t("select_payment_option.card_name")}{" "}
                    <span className="text-[#FF8383]">*</span>
                  </Typography.Title>
                  <Input
                    placeholder={t("select_payment_option.card_name")}
                    className="border-[#074799]"
                    style={{
                      fontSize: "16px",
                      fontFamily: "Plus Jakarta Sans, serif",
                    }}
                  />
                </div> */}

                {/* Expire Date */}
                {/* <div className="col-span-1 flex flex-col">
                  <Typography.Title
                    style={{
                      fontSize: "16px",
                      fontFamily: "Plus Jakarta Sans, serif",
                      color: "#074799",
                      fontWeight: "400",
                    }}
                  >
                    {t("select_payment_option.expiry_date")}{" "}
                    <span className="text-[#FF8383]">*</span>
                  </Typography.Title>
                  <Input
                    placeholder="MM/YY"
                    className="border-[#074799]"
                    style={{
                      fontSize: "16px",
                      fontFamily: "Plus Jakarta Sans, serif",
                    }}
                  />
                </div> */}

                {/* CVV */}
                {/* <div className="col-span-1 flex flex-col">
                  <Typography.Title
                    style={{
                      fontSize: "16px",
                      fontFamily: "Plus Jakarta Sans, serif",
                      color: "#074799",
                      fontWeight: "400",
                    }}
                  >
                    CVV <span className="text-[#FF8383]">*</span>
                  </Typography.Title>
                  <Input
                    placeholder="CVV"
                    className="border-[#074799]"
                    style={{
                      fontSize: "16px",
                      fontFamily: "Plus Jakarta Sans, serif",
                    }}
                  />
                </div> */}
                <CreditSquareupButton
                  setIsComplete={setIsComplete}
                  setPaymentData={setPaymentData}
                  agreePolicy={agreePolicy}
                  paymentInfo={paymentInfo}
                  amount={calculateTotalFee()}
                  handleFormSubmit={handleFormSubmit}
                />
              </div>
            )}
            <AffiliateCodeInput setPaymentInfo={setPaymentInfo} />

            {/* New Limo Service Box */}
            <div className="flex flex-col justify-center items-center border border-blue-200 rounded-xl px-6 py-5 text-base bg-gradient-to-r from-blue-50 to-white shadow-sm hover:shadow-md transition-shadow">
              <div className="w-full flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Image
                      src="https://play-lh.googleusercontent.com/JG6IP-glhBT0E1EVRD2y6YvGNA4kr1ZW2U1HyWxRPhdlRY54Lu1RJeGRWdKWJuFn2lo"
                      alt="Limo Anywhere"
                      preview={false}
                      width={75}
                      height={42}
                      className="object-contain"
                    />
                    {/* <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                      Premium
                    </div> */}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-primary-blue font-semibold text-lg">
                      Limo Service
                    </span>
                    <span className="text-gray-600 text-sm flex items-center gap-1">
                      <span>★★★★★</span>
                      <span>Luxury transportation for your journey</span>
                    </span>
                  </div>
                </div>
                <button
                  className="bg-primary-blue hover:bg-blue-600 text-white font-medium py-2.5 px-6 rounded-lg transition-all transform hover:scale-105 shadow-sm flex items-center gap-1 min-w-24"
                  onClick={() =>
                    window.open(
                      "https://book.mylimobiz.com/v4/traversia",
                      "_blank",
                      "width=1000,height=800"
                    )
                  }
                >
                  <a
                    href="https://book.mylimobiz.com/v4/traversia"
                    // className="bg-primary-blue hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    data-ores-widget="widget"
                    data-ores-alias="traversia"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book Now
                  </a>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Right Side */}
          <div className="flex flex-col gap-4">
            <>
              <p className="text-xl font-semibold text-primary-blue">
                {t("policy.personal_info.collects_info.list8")}
              </p>

              {/* Edit Visa Type */}
              <InfoRow
                key={PaymentDetailFields[0].label}
                label={PaymentDetailFields[0].label}
                value={
                  <Select
                    size="large"
                    className="w-full"
                    value={
                      form.getFieldValue("evisa_entry_type") || "OneMonthSingle"
                    }
                    onChange={handleChangeVisaType}
                  >
                    <Select.Option value="OneMonthSingle">
                      1 month - Single Entry
                    </Select.Option>
                    <Select.Option value="OneMonthMultiple">
                      1 month - Multiple Entries
                    </Select.Option>
                    <Select.Option value="ThreeMonthSingle">
                      3 months - Single Entry
                    </Select.Option>
                    <Select.Option value="ThreeMonthMultiple">
                      3 months - Multiple Entries
                    </Select.Option>
                  </Select>
                }
                editable={false}
              />

              {/* Edit Processing Type */}
              <InfoRow
                key={PaymentDetailFields[1].label}
                label={PaymentDetailFields[1].label}
                value={
                  <Select
                    size="large"
                    className="w-full"
                    value={form.getFieldValue("processing_type") || "Standard"}
                    onChange={handleChangeProcessingType}
                  >
                    <Select.Option value="Standard">
                      Standard (5-10 days)
                    </Select.Option>
                    <Select.Option value="Express">
                      Express (2 days)
                    </Select.Option>
                    <Select.Option value="Urgent">Urgent (1 day)</Select.Option>
                  </Select>
                }
                editable={false}
              />

              {/* Return Date */}
              <InfoRow
                key={PaymentDetailFields[3].label}
                label={PaymentDetailFields[3].label}
                value={PaymentDetailFields[3].value}
                editable={false}
              />
              {/* Date of Entry */}
              <InfoRow
                key={PaymentDetailFields[2].label}
                label={PaymentDetailFields[2].label}
                value={PaymentDetailFields[2].value}
                editable={false}
              />
            </>
            <div className="flex flex-col justify-center items-center border rounded-xl mt-8 px-6 py-2 text-base">
              <p className="font-bold">
                {t("select_payment_option.order_summary")}
              </p>

              <Divider />

              <div className="w-full grid grid-cols-3 text-black gap-4">
                <p className="col-span-1">{t("payment_details.visa_fee")}</p>
                <p className="col-span-1">
                  {t("select_payment_option.order_summary.quantity")}: 1
                </p>
                <p className="col-span-1 text-right text-[#074799] font-bold">
                  {visaEntryTypeFeeMap.get(
                    form.getFieldValue("evisa_entry_type")
                  ) || 0}{" "}
                  $
                </p>

                <p className="col-span-1">
                  {t("select_payment_option.order_summary.processing_fee")}
                </p>
                <p className="col-span-2 text-right text-[#074799] font-bold">
                  {processingTimeFeeMap.get(
                    form.getFieldValue("processing_type")
                  ) || 0}{" "}
                  $
                </p>

                {paymentInfo.voucher_code && (
                  <>
                    <p className="col-span-1">
                      Ref Code: {paymentInfo.voucher_code}
                    </p>
                    <p className="col-span-2 text-right text-[#074799] font-bold">
                      -10 $
                    </p>
                  </>
                )}

                <p className="font-bold text-base col-span-1">
                  {t("select_payment_option.order_summary.total")}
                </p>
                <p className="font-bold text-xl text-right col-span-2 text-primary-blue">
                  {calculateTotalFee()} $
                </p>
              </div>
            </div>

            {/* <div className="flex justify-between items-center border rounded-xl px-6 py-4">
              <p className="font-bold text-base">
                {t("select_payment_option.order_summary.total")}
              </p>

              <p className="font-bold text-xl">63.45 $</p>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetail;
